import _extends from '@babel/runtime/helpers/extends';
import React__default, { useRef, useEffect, useCallback } from 'react';
import { I as InlinePortalProvider } from '../Portal/InlinePortalProvider-DpZ1TsoC.js';
import { b as Shroud, c as SheetContentContainer, d as SheetContentSurface, e as SheetContent } from './Sheet.styles-DmpsK53T.js';
import { C as CloseButtonSection } from './CloseButtonSection-pSVCJ2B6.js';
import { K as KEYBOARD_KEYNAMES } from '../LocalModules/storm-ui-utils/dist/esm/keyboardKeynames-CVa_vjg3-CVa_vjg3.js';
import { t as trapTabFocus } from '../LocalModules/storm-ui-utils/dist/esm/focusableElements/trapTabFocus-CDT7jWDj-DPG2qBEI.js';
import { g as getFocusableElements } from '../LocalModules/storm-ui-utils/dist/esm/focusableElements/getFocusableElements-BjFtn5aB-BjFtn5aB.js';

function createSetBottomToVisualViewport(divRef) {
  return function setBottomToVisualViewport() {
    if (divRef.current instanceof HTMLElement && window.visualViewport) {
      const {
        height: viewportHeight,
        offsetTop: viewportOffsetTop
      } = window.visualViewport;
      const {
        innerHeight
      } = window;
      const divStyle = divRef.current.style;
      // if the viewport is zoomed in (pinch on mobile or trackpad)
      if (innerHeight > viewportHeight) {
        /**
         * set the bottom offset to the window height minus the
         * combined height of the current viewport and its top offset
        */
        divStyle.setProperty('bottom', `${innerHeight - viewportHeight - viewportOffsetTop}px`);
        return;
      }
      divStyle.removeProperty('bottom');
    }
  };
}
const Overlay = ({
  children,
  className,
  closeButton,
  closeButtonProps,
  modalContainerRef,
  onClose,
  padding = 'base',
  side = 'bottom',
  state,
  withCloseButton,
  maxSize = 'default',
  header,
  extraSpace,
  transitionDuration,
  onGetSheetSize,
  zIndex,
  ...rest
}) => {
  const closeRef = useRef(null);
  const sheetRef = useRef(null);
  const sheetContentSurfaceRef = useRef(null);
  useEffect(() => {
    if (sheetContentSurfaceRef.current) {
      var _getComputedStyle;
      onGetSheetSize(parseInt((((_getComputedStyle = getComputedStyle) === null || _getComputedStyle === void 0 || (_getComputedStyle = _getComputedStyle(sheetContentSurfaceRef.current)) === null || _getComputedStyle === void 0 ? void 0 : _getComputedStyle.getPropertyValue(side === 'bottom' ? 'height' : 'width')) || '0px').replace('px', ''), 10), side);
    }
  }, [onGetSheetSize, side]);
  const handleKeyUp = useCallback(event => {
    if (event.key === KEYBOARD_KEYNAMES.ESCAPE) {
      event.preventDefault();
      event.stopPropagation();
      if (onClose) {
        onClose(event);
      }
    }
  }, [onClose]);
  const handleKeyDown = useCallback(event => {
    if (modalContainerRef.current !== null) {
      trapTabFocus(event, modalContainerRef.current);
    }
  }, [modalContainerRef]);
  const handleClick = useCallback(event => {
    const {
      target,
      currentTarget
    } = event;
    if (target === currentTarget) {
      event.preventDefault();
      if (onClose) {
        onClose(event);
      }
    }
  }, [onClose]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (sheetRef.current) {
      const focusableEls = getFocusableElements(sheetRef.current);
      if (focusableEls.length > 0) {
        focusableEls[0].focus();
      }
    }

    // only make the sheet stick to the bottom of the viewport if it's a bottomsheet.
    // other sides don't need to stick to the bottom
    if (side === 'bottom' && typeof window !== 'undefined' && 'visualViewport' in window && window.visualViewport) {
      const {
        visualViewport
      } = window;
      const setBottomToVisualViewport = createSetBottomToVisualViewport(sheetRef);
      visualViewport.addEventListener('resize', setBottomToVisualViewport);
      visualViewport.addEventListener('scroll', setBottomToVisualViewport);
      setBottomToVisualViewport();
      return () => {
        visualViewport.removeEventListener('resize', setBottomToVisualViewport);
        visualViewport.removeEventListener('scroll', setBottomToVisualViewport);
      };
    }
  }, [sheetRef, side]);
  return /*#__PURE__*/React__default.createElement(Shroud, {
    className: className,
    onMouseDown: handleClick,
    onKeyUp: handleKeyUp,
    onKeyDown: handleKeyDown,
    role: "presentation",
    ref: modalContainerRef,
    $fadeState: state,
    $transitionDuration: transitionDuration,
    $zIndex: zIndex
  }, /*#__PURE__*/React__default.createElement(InlinePortalProvider, null, /*#__PURE__*/React__default.createElement(SheetContentContainer, {
    $slideState: state,
    $side: side,
    ref: sheetRef,
    $transitionDuration: transitionDuration
  }, withCloseButton && /*#__PURE__*/React__default.createElement(CloseButtonSection, {
    onClose: onClose,
    closeRef: closeRef,
    closeButtonProps: closeButtonProps,
    closeButton: closeButton,
    side: side
  }), /*#__PURE__*/React__default.createElement(SheetContentSurface, {
    $padding: padding,
    $side: side,
    $maxSize: maxSize,
    $extraSpace: extraSpace,
    $transitionDuration: transitionDuration,
    ref: sheetContentSurfaceRef
  }, header, /*#__PURE__*/React__default.createElement(SheetContent, _extends({}, rest, {
    $padding: padding
  }), children)))));
};

export { Overlay as O };
//# sourceMappingURL=Overlay-Dogkngch.js.map
